/**
 * The site's admin page: who may use OgreClan AI, everyone's accounts, linking (Global Admins) or unlinking a Showdown
 * name, and signing someone out everywhere
 */
import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../api';
import { useMe } from '../me';
import { Link } from '../router';
import { ActionButton, ago, ErrorBox, Loading, toast } from '../ui';

interface AdminUser {
	id: string;
	name: string;
	createdAt: string;
	discord: { id: string, name: string, username: string | null } | null;
	showdown: { id: string, name: string, linkedAt: string, linkedBy: string | null, signIn: boolean } | null;
	sessions: number;
	isAdmin: boolean;
}

/** Global Admins: type a Showdown name to link it to someone's account (no code on Showdown needed) */
function LinkName({ user, reload }: { user: AdminUser, reload: () => Promise<void> }) {
	const [name, setName] = useState('');
	const link = async (move = false): Promise<void> => {
		try {
			const result = await api.post<{ name: string }>(`/api/admin/users/${user.id}/link`, { name, move });
			toast(`Linked ${result.name} to ${user.name}.`, 'good');
			setName('');
			await reload();
		} catch (error) {
			if (error instanceof ApiError && error.status === 409 && !move &&
				confirm(`${error.message} Move it to ${user.name}'s account instead?`)) return link(true);
			toast((error as Error).message, 'bad');
		}
	};
	return <form class="row" style="flex-wrap:nowrap;margin-top:4px" onSubmit={e => {
		e.preventDefault();
		if (name.trim()) void link();
	}}>
		<input class="small" style="width:150px" maxLength={18} placeholder={user.showdown ? 'Change to…' : 'Showdown name'} value={name}
			onInput={e => setName((e.target as HTMLInputElement).value)} aria-label={`Showdown name to link to ${user.name}`} />
		<button type="submit" class="small" disabled={!name.trim()}>Link</button>
	</form>;
}

type AIAccess = 'admins' | 'staff' | 'linked' | 'signed-in';
interface AIAccessSetting {
	access: AIAccess;
	/** An administrator chose it here (otherwise it's the server's own setting, serverDefault) */
	chosen: boolean;
	serverDefault: AIAccess;
	enabled?: boolean;
	freeTier?: boolean;
	privateLeagueData?: boolean;
}
const ACCESS: { value: AIAccess, label: string, about: string }[] = [
	{ value: 'admins', label: 'Admins only', about: `The site's administrators.` },
	{ value: 'staff', label: 'Moderators and admins', about: 'OgreClan staff: draft moderators and administrators.' },
	{ value: 'signed-in', label: 'Everyone', about: 'Anyone signed in to the site, with the usual daily limits.' },
];
const accessName = (access: AIAccess) => ACCESS.find(a => a.value === access)?.label ?? 'Linked Showdown accounts';

/** Who may use OgreClan AI: admins, staff, or everyone signed in (or the server's own setting) */
function AIAccessCard() {
	const [setting, setSetting] = useState<AIAccessSetting | null>(null);
	const [error, setError] = useState<unknown>(null);
	useEffect(() => {
		api.get<AIAccessSetting>('/api/ai/access').then(setSetting, setError);
	}, []);
	const choose = async (access: AIAccess | null) => {
		try {
			const saved = await api.put<AIAccessSetting>('/api/ai/access', { access });
			setSetting(current => ({ ...current, ...saved }));
			toast(`OgreClan AI: ${accessName(saved.access).toLowerCase()} can use it now.`, 'good');
		} catch (e) {
			toast((e as Error).message, 'bad');
		}
	};
	if (error) return <ErrorBox error={error} />;
	if (!setting) return <Loading />;
	return <div class="card">
		<h2 style="margin-top:0">OgreClan AI</h2>
		<p class="muted small">Who can use <Link href="/chat">Ask AI</Link>.{setting.enabled === false && ' It isn\'t set up on the server right now, so nobody can until it is.'}</p>
		<div class="row" role="radiogroup" aria-label="Who can use OgreClan AI">
			{ACCESS.map(choice => <button key={choice.value} type="button" role="radio" aria-checked={setting.access === choice.value}
				class={setting.access === choice.value ? 'primary' : ''}
				onClick={() => setting.access !== choice.value || !setting.chosen ? void choose(choice.value) : undefined}>{choice.label}</button>)}
		</div>
		<p class="muted small" style="margin-bottom:0">
			{ACCESS.find(a => a.value === setting.access)?.about}{' '}
			{setting.chosen
				? <>Chosen here. The server's own setting is <strong>{accessName(setting.serverDefault)}</strong>{' '}
					(<button type="button" class="link small" onClick={() => void choose(null)}>go back to it</button>).</>
				: <>This is the server's own setting (AI_ACCESS) until you choose.</>}
			{' '}Everyone shares one daily budget, and each person has their own daily limit.
			{setting.freeTier && <> It runs on a free model: private leagues stay out of it, and the provider's terms limit opening it to
				everyone (deploy/README.md, OgreClan AI).</>}
		</p>
	</div>;
}

export function AdminPage() {
	const me = useMe();
	const [users, setUsers] = useState<AdminUser[] | null>(null);
	const [error, setError] = useState<unknown>(null);
	const load = () => api.get<{ users: AdminUser[] }>('/api/admin/users').then(result => setUsers(result.users), setError);
	useEffect(() => {
		if (me?.user?.isAdmin) void load();
	}, [me?.user?.isAdmin]);

	if (!me) return null;
	if (!me.user?.isAdmin) return <p class="notice bad">Only the site's administrators can see this page.</p>;
	return <>
		<div class="row" style="justify-content:space-between">
			<h1>Admin</h1>
			<Link href="/admin/linking" class="button">Who hasn't linked</Link>
			<Link href="/admin/scores" class="button">AI roster scores</Link>
		</div>
		<p class="muted">You run every league on the site as staff, private ones too. League setup, deleting and Showdown ranks are
			still done on Showdown.</p>
		<p class="muted small"><strong>View as</strong> shows you the site as that person sees it: their leagues, picks queue, matches
			and replays. It's read-only, and a bar at the top takes you back.</p>
		{me.user.isGlobalAdmin && <p class="muted small">As a Global Admin, you can link a Showdown name to someone's account by typing
			it under Showdown name: they don't need to type a code on Showdown. The account then plays as that name in leagues, and
			gets its rank on OgreClan's server, so check the name first. For someone who can't use Discord, <strong>Let sign in with
			Showdown</strong> lets them sign in to the site as that name (<code>/ogreclan signin</code> on play.ogreclan.com, registered
			names only). It turns off if the name is linked again.</p>}
		<AIAccessCard />
		{error ? <ErrorBox error={error} /> : !users ? <Loading /> : <div class="card"><div class="table-wrap"><table>
			<thead><tr><th>Account</th><th>Discord</th><th>Showdown name</th><th>Joined</th><th class="num">Signed in on</th><th /></tr></thead>
			<tbody>{users.map(user => <tr key={user.id}>
				<td>{user.name}{user.isAdmin && <> <span class="badge accent">Admin</span></>}</td>
				<td>{user.discord ? user.discord.username ? `@${user.discord.username}` :
					<span title={`Discord ID ${user.discord.id}; the @handle shows after their next sign-in`}>{user.discord.name}</span> :
					<span class="muted">—</span>}</td>
				<td>{user.showdown?.name || <span class="muted">Not linked</span>}
					{user.showdown?.linkedBy && <div class="small muted">linked by {user.showdown.linkedBy}</div>}
					{user.showdown?.signIn && <div><span class="badge accent">Signs in with Showdown</span></div>}
					{me.user!.isGlobalAdmin && <LinkName user={user} reload={load} />}</td>
				<td class="nowrap">{ago(user.createdAt)}</td>
				<td class="num">{user.sessions} device{user.sessions === 1 ? '' : 's'}</td>
				<td class="right nowrap">
					{user.id !== me.user!.id && <ActionButton class="small" action={async () => {
						await api.post('/api/admin/view-as', { userId: user.id });
						location.href = '/league';
					}}>View as</ActionButton>}{' '}
					{user.showdown && me.user!.isGlobalAdmin && <ActionButton class="small"
						confirm={user.showdown.signIn ? `Stop ${user.name} signing in with ${user.showdown.name}?` :
							`Let ${user.name} sign in to the site as ${user.showdown.name}, without Discord? Only do this for the person who owns that Showdown name.`}
						action={async () => {
							const allowed = !user.showdown!.signIn;
							await api.post(`/api/admin/users/${user.id}/showdown-sign-in`, { allowed });
							toast(allowed ? `${user.name} can sign in with ${user.showdown!.name} now.` : `${user.name} can't sign in with Showdown now.`, 'good');
							await load();
						}}>{user.showdown.signIn ? 'Stop Showdown sign-in' : 'Let sign in with Showdown'}</ActionButton>}{' '}
					{user.showdown && <ActionButton class="small" confirm={`Unlink ${user.showdown.name} from ${user.name}?`} action={async () => {
						await api.post(`/api/admin/users/${user.id}/unlink`);
						toast(`Unlinked ${user.showdown!.name}.`, 'good');
						await load();
					}}>Unlink</ActionButton>}{' '}
					{user.sessions > 0 && <ActionButton class="small danger" confirm={`Sign ${user.name} out everywhere?`} action={async () => {
						await api.post(`/api/admin/users/${user.id}/sign-out`);
						toast(`Signed ${user.name} out.`, 'good');
						await load();
					}}>Sign out</ActionButton>}
				</td>
			</tr>)}</tbody>
		</table></div></div>}
	</>;
}
